import { useQuery } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useTenant } from "@/features/tenant/context";
import { useI18n } from "@/i18n";
import { api } from "@/lib/api";

interface Digest {
  weekStart: number;
  textEn: string;
  textBn: string;
  source: "ai" | "template";
}

/** Last week's summary on the dashboard, the same one emailed to owners. */
export function WeeklySummaryCard() {
  const { tenantId, can } = useTenant();
  const { t, locale, date } = useI18n();
  const q = useQuery({
    queryKey: ["tenant", tenantId, "digest"],
    queryFn: () => api.get<{ digest: Digest | null }>(`/t/${tenantId}/ai/digest`),
    enabled: can("report.sales.view"),
    staleTime: 3_600_000,
  });
  const d = q.data?.digest;
  // Only last week's summary is worth showing; an older one would mislead.
  if (!d || Date.now() - d.weekStart > 14 * 86_400_000) return null;
  const lines = (locale === "bn" ? d.textBn : d.textEn).split("\n").filter(Boolean);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {d.source === "ai" && <Sparkles className="size-4 text-primary" aria-hidden />}
          {t("aiDigest.title")}
        </CardTitle>
        <CardDescription>
          {t("aiDigest.week", { from: date(d.weekStart), to: date(d.weekStart + 6 * 86_400_000) })}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="list-disc space-y-1 pl-5 text-sm">
          {lines.map((l) => (
            <li key={l}>{l}</li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
